<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { RightPanelOpen16 } from '@carbon/icons-vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ConversationCallButton from './ConversationCallButton.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import {
  useConversationPanel,
  isConversationPanelMounted,
  focusPanelTab,
} from 'dashboard/composables/useConversationPanel';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
// At 1024px with the condensed list and tier-2 open the column is ~349px wide. Below this width the right-hand
// group goes compact (Resolve and the SLA badge show only their icons, the text moves to tooltips) so the name
// keeps room even with SLA and the call button.
const COMPACT_HEADER_WIDTH = 480;
const isCompact = computed(
  () => width.value > 0 && width.value < COMPACT_HEADER_WIDTH
);
const { isAWebWidgetInbox } = useInbox();
// The side panel (Details | Ask Polaris) has its own close; while it is closed the header offers to open it
// on Details, only where a conversation view mounts the panel. In the compact header the offer is an item of
// More actions instead of its own button: at 349px with SLA and call the button left the name ~51px.
const { isOpen: isPanelOpen, showDetails } = useConversationPanel();
const canOpenPanel = computed(
  () => isConversationPanelMounted.value && !isPanelOpen.value
);
const showPanelButton = computed(() => canOpenPanel.value && !isCompact.value);
const openPanel = () => {
  showDetails();
  focusPanelTab();
};

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_participating: 'participating',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const goBack = () => {
  if (backButtonUrl.value) {
    router.push(backButtonUrl.value);
  } else {
    router.go(-1);
  }
};

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(
  () => props.chat?.applied_sla?.id && !currentContact.value?.blocked
);

const copyConversationId = async () => {
  try {
    await copyTextToClipboard(String(props.chat.id));
    useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
  } catch (error) {
    // error
  }
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex h-[4.5rem] shrink-0 items-center gap-2 border-b border-solid border-polaris-border px-4"
  >
    <Button
      v-if="showBackButton"
      v-tooltip="t('GENERAL_SETTINGS.BACK')"
      variant="ghost"
      size="sm"
      icon="i-lucide-arrow-left"
      :aria-label="t('GENERAL_SETTINGS.BACK')"
      @click="goBack"
    />
    <Avatar
      :name="currentContact.name"
      :src="currentContact.thumbnail"
      :size="32"
      :status="currentContact.availability_status"
      hide-offline-status
    />
    <div class="flex min-w-0 flex-1 flex-col items-start overflow-hidden">
      <div class="flex w-full min-w-0 items-center gap-1">
        <span
          class="truncate type-polaris-section-title text-polaris-foreground"
        >
          {{ currentContact.name }}
        </span>
        <Icon
          v-if="!isHMACVerified"
          v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
          icon="i-lucide-triangle-alert"
          class="size-3.5 shrink-0 text-polaris-warning-foreground"
        />
      </div>
      <div
        class="flex min-w-0 items-center gap-1.5 truncate text-xs text-polaris-muted-foreground"
      >
        <button
          type="button"
          class="shrink-0 !p-0 hover:text-polaris-foreground"
          @click="copyConversationId"
        >
          {{ `#${chat.id}` }}
        </button>
        <template v-if="hasMultipleInboxes">
          <span class="shrink-0">·</span>
          <InboxName :inbox="inbox" class="!mx-0" />
        </template>
        <template v-if="isSnoozed">
          <span class="shrink-0">·</span>
          <span class="truncate font-medium text-polaris-warning-foreground">
            {{ snoozedDisplayText }}
          </span>
        </template>
      </div>
    </div>
    <div class="flex shrink-0 items-center gap-2">
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        :compact="isCompact"
        class="hidden md:flex"
      />
      <ConversationCallButton :inbox="inbox" :chat="currentChat" />
      <MoreActions
        :conversation-id="currentChat.id"
        :compact="isCompact"
        :show-details-action="canOpenPanel && isCompact"
        @show-details="openPanel"
      />
      <Button
        v-if="showPanelButton"
        v-tooltip="t('CONVERSATION.HEADER.SHOW_DETAILS')"
        variant="ghost"
        size="sm"
        :aria-label="t('CONVERSATION.HEADER.SHOW_DETAILS')"
        data-conversation-panel-opener
        @click="openPanel"
      >
        <template #icon>
          <RightPanelOpen16 aria-hidden="true" />
        </template>
      </Button>
    </div>
  </div>
</template>
